import { Quote } from "lucide-react";
import prosperLogo from "@/assets/prosper-with-purpose-logo.png";

type Testimonial = {
  id: number;
  quote: string;
  author: string;
  role: string;
  company: string;
  image?: string;
  logo?: string;
  website?: string;
};

const testimonials: Testimonial[] = [
  {
    id: 1,
    quote:
      "AuthorPro has powered the entire B2B Culture Coach platform. My Culture Pack system is now fully digitised, interactive, and personalised for every client. The platform delivers tailored culture insights, business diagnostics, and practical improvement paths without adding complexity to my workflow.",
    author: "Nik Dewar",
    role: "Founder, B2B Culture Coach",
    company: "Culture and Performance Consultant",
    image: "/lovable-uploads/nik-dewar.png",
    website: "https://b2bculturecoach.com/",
  },
  {
    id: 2,
    quote:
      "The Prosper With Purpose platform is now fully interactive because of AuthorPro. Readers can complete their business evaluation online and receive a personalised report that links directly to the principles in the book. The system turns purpose into practical action.",
    author: "Colin Crooks MBE",
    role: "Author, Prosper With Purpose",
    company: "Social Entrepreneur and Impact Advocate",
    image: "/lovable-uploads/colin-crooks.webp",
    logo: prosperLogo,
    website: "https://prosperwithpurpose.ai/",
  },
  {
    id: 3,
    quote:
      "AuthorPro helped me turn the science behind hearing and brain health into an interactive experience that patients can actually use. Instead of only reading the book, people complete a personalised assessment and receive clear, actionable steps. It has increased the number of people we can help worldwide.",
    author: "Dr Robert Keefer",
    role: "Author, Hear Better, Live Longer",
    company: "Hearing Health and Cognitive Wellness Advocate",
    image: "/lovable-uploads/dr-robert-keefer.png",
    website: "https://www.hearbetterlivelonger.com/",
  },
  {
    id: 4,
    quote:
      "AuthorPro helped us convert our in person Wealth Engineering workshops into a seamless online learning system. Participants access structured lessons, tools, and personalised insights from anywhere. It has elevated the way we deliver financial education.",
    author: "William Parker",
    role: "Founder",
    company: "Wealth Engineering Academy",
    image: "/lovable-uploads/william-parker.jpg",
    website: "https://app.thewealthengineer.com/",
  },
];

const CaseStudies = () => {
  return (
    <section id="case-studies" aria-labelledby="case-studies-heading" className="py-20 md:py-28">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Case studies</p>
            </div>
            <h2 id="case-studies-heading" className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              Authors who turned a book into a working business.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-sm md:text-right">
            Live platforms built on published books — each one earning attention, insight and revenue
            for its author.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {testimonials.map((t) => (
            <figure key={t.id} className="card-noir-hover group relative p-7 md:p-8 flex flex-col">
              <Quote className="absolute top-6 right-6 h-7 w-7 text-gold/25" aria-hidden />

              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-border bg-background">
                  {t.image ? (
                    <img
                      src={t.image}
                      alt={`${t.author}, ${t.role}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : t.logo ? (
                    <img src={t.logo} alt={`${t.company} logo`} className="h-full w-full object-contain p-2" loading="lazy" />
                  ) : null}
                </div>
                <div>
                  <figcaption className="font-serif text-lg leading-tight">{t.author}</figcaption>
                  <p className="text-[13px] text-gold">{t.role}</p>
                </div>
              </div>

              <blockquote className="font-serif italic text-[15px] leading-[1.75] text-foreground/80 mb-6">
                “{t.quote}”
              </blockquote>

              <div className="mt-auto pt-5 border-t border-border">
                {t.website ? (
                  <a
                    href={t.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-gold transition-colors"
                  >
                    {t.company} →
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground">{t.company}</span>
                )}
              </div>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
